import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUsers, FaStar } from 'react-icons/fa';

const Members = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const membersRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (membersRef.current) observer.observe(membersRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const cacheKey = 'rolesData';
    const cacheTimestampKey = 'rolesDataTimestamp';
  
    const fetchRoles = async () => {
      try {
        const cachedData = localStorage.getItem(cacheKey);
        const cachedTimestamp = localStorage.getItem(cacheTimestampKey);
  
        const isCacheValid = cachedTimestamp && (new Date().getTime() - new Date(parseInt(cachedTimestamp)).getTime()) < 1 * 3600 * 1000;
  
        if (cachedData && isCacheValid) {
          setRoles(JSON.parse(cachedData));
        } else {
          const response = await fetch('https://hub.mileageriot.com/api/roles');
          const data = await response.json();
          const rolesWithUsers = data.filter(role => role.users && role.users.length > 0);
          setRoles(rolesWithUsers);
          localStorage.setItem(cacheKey, JSON.stringify(rolesWithUsers));
          localStorage.setItem(cacheTimestampKey, new Date().getTime().toString());
        }
      } catch (error) {
        console.error('Error fetching roles data:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchRoles();
  }, []);

  if (loading) {
    return (
      <section className="members-modern page-members" ref={membersRef}>
        <div className="container">
          <div className="text-center">
            <div className="loading-spinner">
              <div className="spinner-modern"></div>
            </div>
            <p className="mt-3 text-white-50">{t('members.loading') || 'Загружаем команду...'}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="members-modern page-members" ref={membersRef}>
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center mb-5 fade-in-up stagger-1">
          <div className="section-badge">
            <FaUsers className="me-2" />
            <span>{t('members.badge') || 'Команда'}</span>
          </div>
          <h2 className="section-title text-glow text-white">
            {t('members.title') || 'Знакомство с командой'}
          </h2>
          <div className="section-decoration mx-auto"></div>
          <p className="text-white-50 mt-3 section-subtitle">
            {t('members.subtitle') || 'Наши преданные профессионалы, работающие над улучшением нашего сообщества'}
          </p>
        </div>

        {/* Roles Sections */}
        <div className="members-content">
          {roles.map((role, index) => (
            <div key={role.id} className="role-section fade-in-up" style={{ animationDelay: `${index * 0.2}s` }}>
              <div className="role-header">
                <div className="role-title-container">
                  {role.icon && (
                    <img 
                      src={`https://hub.mileageriot.com/storage/roles/${role.icon}`} 
                      width="50" 
                      alt={`${role.name} Icon`}
                      className="role-icon"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <h3 className="role-title text-white">{role.name}</h3>
                </div>
                <div className="role-divider"></div>
              </div>
              
              {role.another_description && (
                <div className="role-description">
                  <p className="text-white-50">{role.another_description}</p>
                </div>
              )}
              
              <div className="members-grid">
                {role.users.map((user, userIndex) => (
                  <div 
                    key={user.id} 
                    className="member-card glass-container"
                    style={{ animationDelay: `${(index * 0.2) + (userIndex * 0.1)}s` }}
                  >
                    <div className="member-avatar-container">
                      <img 
                        className="member-avatar"
                        src={user.avatar} 
                        alt={user.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(user.name) + '&color=00cfe8&background=1d1e26&size=128';
                        }}
                      />
                      <div className="member-role-badge">
                        <FaStar className="member-badge-icon" />
                      </div>
                    </div>
                    <div className="member-info">
                      <h4 className="member-name text-white">{user.name}</h4>
                      <span className="member-role text-primary">{role.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="members-bg-decoration"></div>
    </section>
  );
};

export default Members;